"use client"

import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Badge,
  Row,
  Col,
} from "reactstrap"
import { Btn } from "../../../AbstractElements"
import type { Menu } from "../rolemanagement/types"

interface MenuDetailModalProps {
  isOpen: boolean
  onClose: () => void
  menu: Menu | null
}

export default function MenuDetailModal({ isOpen, onClose, menu }: MenuDetailModalProps) {
  if (!menu) return null

  const getMenuTypeColor = (type: string) => {
    switch (type) {
      case "section":
        return "primary"
      case "sub":
        return "info"
      case "link":
        return "success"
      default:
        return "secondary"
    }
  }

  const getStatusColor = (isActive: boolean) => {
    return isActive ? "success" : "secondary"
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        <div className="d-flex align-items-center">
          {menu.icon && <i className={`${menu.icon} me-2`}></i>}
          <span className="me-2">Menu Details</span>
          <Badge color={getStatusColor(menu.is_active)} pill>
            {menu.is_active ? "Active" : "Inactive"}
          </Badge>
        </div>
      </ModalHeader>

      <ModalBody>
        {/* Basic Information */}
        <Card className="mb-3">
          <CardHeader>
            <CardTitle tag="h6" className="mb-0">
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md="6">
                <div className="mb-3">
                  <label className="form-label fw-bold text-muted small">INTERNAL NAME</label>
                  <div className="fs-6 font-monospace text-primary p-2 rounded">{menu.name}</div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold text-muted small">DISPLAY TITLE</label>
                  <div className="fs-5">{menu.title}</div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold text-muted small">TYPE</label>
                  <div>
                    <Badge color={getMenuTypeColor(menu.menu_type)} pill className="px-3 py-2 text-capitalize">
                      {menu.menu_type}
                    </Badge>
                  </div>
                </div>
              </Col>
              <Col md="6">
                <div className="mb-3">
                  <label className="form-label fw-bold text-muted small">PARENT MENU</label>
                  <div>{menu.parent_name || <span className="text-muted">Root Level</span>}</div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold text-muted small">SORT ORDER</label>
                  <div>
                    <Badge color="secondary" outline className="px-3 py-2">
                      {menu.sort_order}
                    </Badge>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold text-muted small">HIERARCHY</label>
                  <div className="small">
                    {menu.url || (menu.parent_name ? `${menu.parent_name} > ${menu.title}` : menu.title)}
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Configuration */}
        <Card className="mb-3">
          <CardHeader>
            <CardTitle tag="h6" className="mb-0">
              Configuration
            </CardTitle>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md="6">
                <div className="mb-3">
                  <label className="form-label fw-bold text-muted small">URL PATH</label>
                  <div>
                    {menu.url ? (
                      <code className="bg-light px-3 py-2 rounded d-inline-block">{menu.url}</code>
                    ) : (
                      <span className="text-muted">No URL specified</span>
                    )}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold text-muted small">ICON</label>
                  <div>
                    {menu.icon ? (
                      <span>
                        <i className={menu.icon}></i> <code className="ms-2">{menu.icon}</code>
                      </span>
                    ) : (
                      <span className="text-muted">No icon</span>
                    )}
                  </div>
                </div>
              </Col>
              <Col md="6">
                <div className="mb-3">
                  <label className="form-label fw-bold text-muted small">LANGUAGE CLASS</label>
                  <div>
                    {menu.lan_class ? (
                      <code className="bg-light px-2 py-1 rounded">{menu.lan_class}</code>
                    ) : (
                      <span className="text-muted">None</span>
                    )}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold text-muted small">VISIBILITY</label>
                  <div className="d-flex gap-2">
                    <Badge color={menu.is_active ? "success" : "secondary"} pill>
                      {menu.is_active ? "Active" : "Inactive"}
                    </Badge>
                    <Badge color={menu.is_visible ? "info" : "secondary"} pill>
                      {menu.is_visible ? "Visible" : "Hidden"}
                    </Badge>
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Timestamps */}
        <Card className="mb-3">
          <CardHeader>
            <CardTitle tag="h6" className="mb-0">
              Timestamps
            </CardTitle>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md="6">
                <div className="mb-3">
                  <label className="form-label fw-bold text-muted small">CREATED</label>
                  <div>{formatDate(menu.created_at)}</div>
                </div>
              </Col>
              <Col md="6">
                <div className="mb-3">
                  <label className="form-label fw-bold text-muted small">LAST UPDATED</label>
                  <div>{formatDate(menu.updated_at)}</div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Child Menus */}
        {menu.children && menu.children.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle tag="h6" className="mb-0">
                Child Menus ({menu.children.length})
              </CardTitle>
            </CardHeader>
            <CardBody>
              <div className="d-flex flex-wrap gap-2">
                {menu.children.map((child) => (
                  <div key={child.id} className="border rounded p-2 text-primary flex-fill mb-2">
                    <div className="d-flex align-items-center">
                      {child.icon && <i className={`${child.icon} me-2`}></i>}
                      <span className="fw-medium">{child.title}</span>
                      <Badge color={getMenuTypeColor(child.menu_type)} size="sm" className="ms-2">
                        {child.menu_type}
                      </Badge>
                    </div>
                    {child.url && <small className="text-muted">{child.url}</small>}
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}
      </ModalBody>

      <ModalFooter>
        <Btn color="secondary" onClick={onClose}>
          Close
        </Btn>
      </ModalFooter>
    </Modal>
  )
}
